import { cn } from '@sr/ui-library';
import React from 'react';
import { formatCappedTotal } from '../utils/helpers/cappedTotal';
import { CountBadge } from './CountBadge';
import { MSR3Empty, MSR3EmptyProps } from './MSR3Empty';

const EntityListRoot = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      {children}
    </div>
  );
};

EntityListRoot.displayName = 'EntityList';

const EntityListHeader = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex items-center gap-2', className)} {...props}>
      {children}
    </div>
  );
};

EntityListHeader.displayName = 'EntityList.Header';

const EntityListTitle = ({
  children,
  className,
  ...props
}: React.ComponentProps<'h2'>) => {
  return (
    <h2
      className={cn('text-base font-medium text-gray-800', className)}
      {...props}
    >
      {children}
    </h2>
  );
};

EntityListTitle.displayName = 'EntityList.Title';

interface EntityListCountProps extends React.ComponentProps<typeof CountBadge> {
  count: number;
  /** `count` is a floor (a capped total), so it reads "5,000+". */
  isCapped?: boolean;
}

const EntityListCount = ({
  className,
  count = 0,
  isCapped,
  ...props
}: EntityListCountProps) => {
  if (count === 0) return null;

  // Same badge as the Tasks tabs: it grows with the digits, so 1000+ is not cut off.
  return (
    <CountBadge className={className} {...props}>
      {/* CountBadge adds the separators to a bare number only. */}
      {isCapped ? formatCappedTotal(count, true) : count}
    </CountBadge>
  );
};

EntityListCount.displayName = 'EntityList.Count';

const EntityListActions = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={cn('ml-auto flex items-center gap-2', className)}
      {...props}
    >
      {children}
    </div>
  );
};

EntityListActions.displayName = 'EntityList.Actions';

const EntityListContent = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div className={cn('flex flex-col gap-3', className)} {...props}>
      {children}
    </div>
  );
};

EntityListContent.displayName = 'EntityList.Content';

type EntityListEmptyProps = MSR3EmptyProps;
const EntityListEmpty = (props: EntityListEmptyProps) => (
  <MSR3Empty {...props} />
);

EntityListEmpty.displayName = 'EntityList.Empty';

// Export types for TypeScript users
export type { EntityListCountProps, EntityListEmptyProps };

const EntityList = Object.assign(EntityListRoot, {
  Actions: EntityListActions,
  Content: EntityListContent,
  Count: EntityListCount,
  Empty: EntityListEmpty,
  Header: EntityListHeader,
  Title: EntityListTitle,
});

export { EntityList };
